import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import type { NotificationChannel, NotificationKind } from '@pirouette/shared';
import { defaultPreference, preferenceKey } from '@pirouette/shared';
import { useNotificationPreferences, useSetNotificationPreference } from '@/data/messaging';
import { env } from '@/env';
import {
  ensurePushSubscription,
  getPushPermission,
  pushIsSupported,
  registerPushSubscriptionWithServer,
  unregisterPushSubscription,
} from '@/lib/webPush';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { SettingsNav } from '@/components/settings/SettingsNav';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/parent/settings/notifications')({
  component: NotificationSettings,
});

const KINDS: { value: NotificationKind; label: string; description: string }[] = [
  {
    value: 'message',
    label: 'Direct messages',
    description: 'When the studio replies to your thread.',
  },
  {
    value: 'broadcast',
    label: 'Studio announcements',
    description: 'Class-wide or studio-wide notes.',
  },
  {
    value: 'schedule_change',
    label: 'Schedule changes',
    description: 'Cancellations, room changes, makeups.',
  },
  { value: 'payment_due', label: 'Bills due', description: 'Upcoming tuition or ticket charges.' },
  {
    value: 'payment_failed',
    label: 'Payment problems',
    description: 'When auto-pay fails or a card is declined.',
  },
  {
    value: 'costume_due',
    label: 'Costume reminders',
    description: 'Fittings, payments, and pickup dates.',
  },
  {
    value: 'comp_update',
    label: 'Competition updates',
    description: 'Itinerary, call times, results.',
  },
  {
    value: 'enrollment',
    label: 'Enrollment',
    description: 'Waitlist promotions, drop confirmations.',
  },
];

const CHANNELS: { value: NotificationChannel; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'push', label: 'Push' },
  { value: 'in_app', label: 'In-app' },
];

function NotificationSettings() {
  const prefsQuery = useNotificationPreferences();
  const setPref = useSetNotificationPreference();
  const [pushStatus, setPushStatus] = useState<ReturnType<typeof getPushPermission>>('default');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    setPushStatus(getPushPermission());
  }, []);

  const prefMap = new Map<string, boolean>();
  for (const p of prefsQuery.data ?? []) {
    prefMap.set(preferenceKey(p.kind, p.channel), p.enabled);
  }

  function isEnabled(kind: NotificationKind, channel: NotificationChannel): boolean {
    const k = preferenceKey(kind, channel);
    if (prefMap.has(k)) return prefMap.get(k)!;
    return defaultPreference(kind, channel);
  }

  async function toggle(kind: NotificationKind, channel: NotificationChannel) {
    const current = isEnabled(kind, channel);
    try {
      await setPref.mutateAsync({ kind, channel, enabled: !current });
    } catch (err) {
      toast({
        title: "Couldn't update preference",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  async function handleEnablePush() {
    setSubscribing(true);
    try {
      const sub = await ensurePushSubscription(env.VITE_VAPID_PUBLIC_KEY ?? '');
      if (!sub) {
        toast({
          title: 'Push not enabled',
          description: 'You may need to allow notifications in your browser.',
          variant: 'error',
        });
        return;
      }
      await registerPushSubscriptionWithServer(sub);
      setPushStatus('granted');
      toast({ title: 'Push enabled', variant: 'success' });
    } finally {
      setSubscribing(false);
    }
  }

  async function handleDisablePush() {
    setSubscribing(true);
    try {
      await unregisterPushSubscription();
      setPushStatus('default');
      toast({ title: 'Push disabled', variant: 'success' });
    } finally {
      setSubscribing(false);
    }
  }

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Settings
        </p>
        <h1 className="mt-2">Notifications</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Choose how the studio reaches you. Time-critical items (schedule changes, payment
          failures) default to SMS — you can turn that off, but think twice.
        </p>
        <SettingsNav className="mt-4" />
      </header>

      <section aria-labelledby="push-section" className="surface-elevated p-6">
        <h2 id="push-section" className="font-medium text-[var(--text-lg)]">
          Browser push
        </h2>
        <p className="mt-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
          Works on desktop and Android in Chrome / Edge / Firefox. iOS Safari supports push only
          after installing the PWA to the home screen.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {!pushIsSupported() && <Badge tone="warn">unsupported in this browser</Badge>}
          {pushIsSupported() && pushStatus === 'granted' && <Badge tone="success">enabled</Badge>}
          {pushIsSupported() && pushStatus === 'denied' && (
            <Badge tone="danger">blocked — adjust in browser settings</Badge>
          )}
          {pushIsSupported() && pushStatus === 'default' && (
            <Button onClick={() => void handleEnablePush()} disabled={subscribing}>
              {subscribing ? 'Working…' : 'Enable push notifications'}
            </Button>
          )}
          {pushIsSupported() && pushStatus === 'granted' && (
            <Button variant="ghost" onClick={() => void handleDisablePush()} disabled={subscribing}>
              Disable
            </Button>
          )}
        </div>
      </section>

      <section aria-labelledby="prefs-section">
        <h2 id="prefs-section" className="text-[var(--text-xl)]">
          Choose channels per topic
        </h2>

        <div
          role="region"
          aria-label="Notification preferences"
          className="surface mt-4 overflow-hidden"
        >
          <table className="w-full text-left">
            <thead className="bg-[var(--color-surface-sunken)] text-[var(--text-sm)]">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium">
                  Topic
                </th>
                {CHANNELS.map((c) => (
                  <th key={c.value} scope="col" className="px-4 py-3 text-center font-medium">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {KINDS.map((k) => (
                <tr key={k.value} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-3">
                    <p className="font-medium">{k.label}</p>
                    <p className="mt-0.5 text-[var(--color-ink-muted)] text-[var(--text-xs)]">
                      {k.description}
                    </p>
                  </td>
                  {CHANNELS.map((c) => (
                    <td key={c.value} className="px-4 py-3 text-center">
                      <label className="inline-flex cursor-pointer items-center justify-center">
                        <span className="sr-only">
                          {k.label} via {c.label}
                        </span>
                        <input
                          type="checkbox"
                          className="size-4 accent-[var(--color-accent)]"
                          checked={isEnabled(k.value, c.value)}
                          onChange={() => void toggle(k.value, c.value)}
                          disabled={setPref.isPending}
                        />
                      </label>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-3 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
          In-app notifications appear on your dashboard regardless of these settings.
        </p>
      </section>
    </div>
  );
}
